import { Command as CommandPrimitive } from 'cmdk';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cn } from '../cn';
import { SearchIcon } from '../icons';

export const Command = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CommandPrimitive>>(function Command(
  { className, ...props },
  ref,
) {
  return <CommandPrimitive ref={ref} className={cn('flex h-full w-full flex-col overflow-hidden bg-surface text-ink', className)} {...props} />;
});

export const CommandInput = forwardRef<HTMLInputElement, ComponentPropsWithoutRef<typeof CommandPrimitive.Input>>(
  function CommandInput({ className, ...props }, ref) {
    return (
      <div className="flex items-center gap-2 border-b border-border px-3">
        <SearchIcon size={15} className="text-ink-muted" />
        <CommandPrimitive.Input
          ref={ref}
          className={cn('h-10 w-full bg-transparent text-body text-ink outline-none placeholder:text-ink-muted', className)}
          {...props}
        />
      </div>
    );
  },
);

export const CommandList = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CommandPrimitive.List>>(
  function CommandList({ className, ...props }, ref) {
    return <CommandPrimitive.List ref={ref} className={cn('max-h-96 overflow-y-auto overscroll-contain p-1', className)} {...props} />;
  },
);

export const CommandEmpty = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>>(
  function CommandEmpty({ className, ...props }, ref) {
    return <CommandPrimitive.Empty ref={ref} className={cn('px-3 py-6 text-body text-ink-muted', className)} {...props} />;
  },
);

export const CommandGroup = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CommandPrimitive.Group>>(
  function CommandGroup({ className, ...props }, ref) {
    return (
      <CommandPrimitive.Group
        ref={ref}
        className={cn(
          'py-1 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-meta [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-ink-muted',
          className,
        )}
        {...props}
      />
    );
  },
);

export const CommandItem = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CommandPrimitive.Item>>(
  function CommandItem({ className, ...props }, ref) {
    return (
      <CommandPrimitive.Item
        ref={ref}
        className={cn(
          'flex min-h-8 items-center gap-2 rounded-sm px-2 py-1 text-body text-ink outline-none select-none data-[disabled=true]:opacity-50 data-[selected=true]:bg-surface-muted',
          className,
        )}
        {...props}
      />
    );
  },
);

export const CommandSeparator = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>>(
  function CommandSeparator({ className, ...props }, ref) {
    return <CommandPrimitive.Separator ref={ref} className={cn('-mx-1 my-1 h-px bg-border', className)} {...props} />;
  },
);

export const CommandLoading = CommandPrimitive.Loading;


/** Command palette shell (Ctrl/⌘+K): modal, top-anchored, 640px. */
export function CommandDialog({ open, onOpenChange, title, children }: { open: boolean; onOpenChange: (open: boolean) => void; title: string; children: ReactNode }) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-(--z-dialog) bg-scrim data-[state=open]:animate-fade-in" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed start-1/2 top-[14vh] z-(--z-dialog) w-[calc(100vw-32px)] max-w-160 -translate-x-1/2 overflow-hidden rounded-md border border-border bg-surface shadow-overlay outline-none rtl:translate-x-1/2"
        >
          <DialogPrimitive.Title className="sr-only">{title}</DialogPrimitive.Title>
          {children}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
